import {
  AnswerPayload,
  BookingQuestion,
  QuestionPayload,
  SaveBookingQuestionRequest,
} from '@/api/booking-types'

import { QuestionFormAnswer, QuestionFormData } from './question-form-modal.constants'

interface BuildSaveQuestionRequestParams {
  bookingTypeId: number
  defaultAnswer: { placeholder: string; title: string }
  existing?: BookingQuestion
  nextOrder: number
  values: QuestionFormData
}

const toButtonAnswer = (answer: QuestionFormAnswer, order: number): AnswerPayload => ({
  order,
  placeholder: answer.placeholder,
  pricingType: answer.pricingType,
  pricingValue: answer.pricingValue,
  title: answer.title.trim(),
  type: 'button',
})

// Note: non-button types (input, image, time picker) carry exactly one synthesized answer that only stores the type
export const buildSaveQuestionRequest = ({
  bookingTypeId,
  defaultAnswer,
  existing,
  nextOrder,
  values,
}: BuildSaveQuestionRequestParams): SaveBookingQuestionRequest => {
  const question: QuestionPayload = {
    imageLimit: values.answersType === 'image' ? Number(values.imageLimit) : null,
    isRequired: values.isRequired,
    modal: values.modal.trim(),
    order: existing?.order ?? nextOrder,
    selectType: values.selectType,
    title: values.title.trim(),
  }

  const existingAnswers = existing?.answers ?? []
  const base = { bookingTypeId, question, questionId: existing?.id }

  if (values.answersType === 'button') {
    const keptIds = new Set(values.answers.flatMap(({ id }) => (id === undefined ? [] : [id])))

    return {
      ...base,
      answers: {
        create: values.answers.flatMap((answer, index) =>
          answer.id === undefined ? [toButtonAnswer(answer, index)] : [],
        ),
        deleteIds: existingAnswers.filter(({ id }) => !keptIds.has(id)).map(({ id }) => id),
        update: values.answers.flatMap((answer, index) =>
          answer.id === undefined ? [] : [{ ...toButtonAnswer(answer, index), id: answer.id }],
        ),
      },
    }
  }

  const kept = existingAnswers.find(({ type }) => type === values.answersType)

  return {
    ...base,
    answers: {
      create: kept
        ? []
        : [
            {
              order: 0,
              placeholder: defaultAnswer.placeholder,
              pricingType: 'add',
              pricingValue: '',
              title: defaultAnswer.title,
              type: values.answersType,
            },
          ],
      deleteIds: existingAnswers.filter(({ id }) => id !== kept?.id).map(({ id }) => id),
      update: [],
    },
  }
}
